/**
 * FreshnessBadge — Badge de fraîcheur pour modules à analyses datées.
 *
 * Doctrine 47 (inversion de Munger) — On rend visible ce qui pourrait
 * tromper l'utilisateur : un module qui affiche encore une analyse vieille
 * de 3 mois sans signal de date risque de produire des décisions sur des
 * données obsolètes. Le badge protège la crédibilité pendant que la
 * dynamisation de fond (cron, refresh auto) se construit module par module.
 *
 * Trois états :
 *   - "Frais"        — < 7 jours       — vert  (#1D9E75)
 *   - "Vieillissant" — 7-30 jours       — orange (#EF9F27)
 *   - "Obsolète"     — > 30 jours       — rouge (#E24B4A)
 *
 * Props :
 *   - lastGeneratedAt : ISO string ou null/undefined (rien rendu si absent)
 *   - onRegenerate    : callback bouton "Régénérer" (optionnel)
 *   - regenerating    : bool — affiche un spinner si true
 *   - testid          : data-testid (défaut "freshness-badge")
 *   - size            : "sm" (défaut) | "md"
 *   - moduleLabel     : optionnel, affiché en tooltip ("Asymétries", etc.)
 */
import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertTriangle, AlertCircle, RefreshCw, Loader2 } from "lucide-react";

function diffDays(iso) {
  try {
    const then = new Date(iso).getTime();
    if (!then || Number.isNaN(then)) return null;
    return Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function FreshnessBadge({
  lastGeneratedAt,
  onRegenerate,
  regenerating = false,
  testid = "freshness-badge",
  size = "sm",
  moduleLabel = "",
}) {
  const { t } = useTranslation();
  if (!lastGeneratedAt) return null;
  const days = diffDays(lastGeneratedAt);
  if (days === null) return null;

  let state, color, bg, border, Icon, label;
  if (days < 7) {
    state = "fresh"; color = "#1D9E75"; Icon = CheckCircle2;
    label = t("freshness.fresh", { defaultValue: "Frais" });
  } else if (days <= 30) {
    state = "aging"; color = "#EF9F27"; Icon = AlertTriangle;
    label = t("freshness.aging", { defaultValue: "Vieillissant" });
  } else {
    state = "stale"; color = "#E24B4A"; Icon = AlertCircle;
    label = t("freshness.stale", { defaultValue: "Obsolète" });
  }
  bg = `${color}1A`;
  border = `${color}55`;

  const px = size === "md" ? "px-2.5 py-1" : "px-2 py-0.5";
  const iconSize = size === "md" ? "w-3.5 h-3.5" : "w-3 h-3";
  const textSize = size === "md" ? "text-[11px]" : "text-[10px]";

  const tooltip = `${moduleLabel ? moduleLabel + " · " : ""}${t("freshness.generated", { defaultValue: "Généré le" })} ${fmtDate(lastGeneratedAt)} (${days} ${t("freshness.days", { defaultValue: "j" })})`;

  return (
    <span
      className="inline-flex items-center gap-1.5"
      data-testid={testid}
      data-freshness-state={state}
    >
      <span
        className={`inline-flex items-center gap-1 rounded ${px} ${textSize} font-medium border`}
        style={{ background: bg, color, borderColor: border }}
        title={tooltip}
        data-testid={`${testid}-pill`}
      >
        <Icon className={iconSize} />
        <span>{label}</span>
        <span className="text-zinc-500 font-normal">· {days}{t("freshness.days_suffix", { defaultValue: "j" })}</span>
      </span>
      {onRegenerate && (
        <button
          type="button"
          onClick={onRegenerate}
          disabled={regenerating}
          className="inline-flex items-center gap-1 text-[10px] text-zinc-400 hover:text-white disabled:opacity-50 px-1.5 py-0.5 rounded hover:bg-white/5"
          title={t("freshness.regenerate_hint", { defaultValue: "Forcer un rafraîchissement immédiat" })}
          data-testid={`${testid}-regenerate`}
        >
          {regenerating ? <Loader2 className={`${iconSize} animate-spin`} /> : <RefreshCw className={iconSize} />}
          {t("freshness.regenerate", { defaultValue: "Régénérer" })}
        </button>
      )}
    </span>
  );
}
